import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import CustomDateFilter from "components/shared-components/RemoteTable/custom-date-filter";
import CustomSelectFilter from "components/shared-components/RemoteTable/custom-select-filter";
import { getUsersWithCache, cacheUsers } from "./UserCache";
import ExportAuditLogsModal from "./ExportAuditLogsModal";
import {
  CUSTOM_DATE_FILTER_TABLE_TYPE,
  CUSTOM_SELECT_FILTER_TABLE_TYPE,
  DELAY_FILTER_TIME,
} from "constants/RemoteDataTableContanst";
import {
  useListMsplogisticAuditLogs,
  useLogAdminUsers,
} from "hooks/audit-logs/useAuditLogs";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  selectFilter,
  customFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Input,
  FormGroup,
  Label,
} from "reactstrap";

import {
  getInitAuditLogsParams,
  auditLogActions,
  auditLogTypes,
} from "./utils";
import DetailAuditLogs from "./DetailAuditLogs";
import auditLogsService, {
  AUDIT_TRACKING_TOKEN_KEY,
} from "services/auditLogsService";
import { useToasts } from "react-toast-notifications";

const ListAuditLogs = props => {
  const { t } = useTranslation(["auditLogs"]);
  const { addToast } = useToasts();
  const [mode, setMode] = useState("");
  const [columnsTable, setColumnsTable] = useState([]);
  const [dataDetail, setDataDetail] = useState({});
  const [detailModal, setDetailModal] = useState(false);
  const [auditLogsParams, setAuditLogsParams] = useState(
    getInitAuditLogsParams()
  );
  const [usersMap, setUsersMap] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { data: logAdminUsers } = useLogAdminUsers();

  const [exportModal, setExportModal] = useState(false);
  const toggleExportModal = () => {
    setExportModal(!exportModal);
  };

  const handleExport = exportParams => {
    auditLogsService
      .exportAuditLogs({
        params: exportParams,
      })
      .then(response => {
        const { data } = response;

        if (!data || !(data instanceof Blob) || data.size === 0) {
          addToast(t("message.export_error"), {
            appearance: "error",
            autoDismiss: true,
          });
          return;
        }

        const linkDownload = document.createElement("a");
        linkDownload.href = URL.createObjectURL(data);
        linkDownload.setAttribute(
          "download",
          `audit_logs_${
            exportParams.columnFilter?.["filter[maker_date]"] || "all"
          }.xlsx`
        );
        document.body.appendChild(linkDownload);
        linkDownload.click();

        addToast(t("message.export_success"), {
          appearance: "success",
          autoDismiss: true,
        });
      })
      .catch(error => {
        console.error("Export failed:", error);
        addToast(t("message.export_error"), {
          appearance: "error",
          autoDismiss: true,
        });
      });
  };

  const debounceTimeout = useRef(null);

  const {
    data: auditLogsList,
    refetch: listAuditLogs,
    isLoading,
  } = useListMsplogisticAuditLogs({
    params: auditLogsParams,
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (!auditLogsList?.items || auditLogsList.items.length === 0) {
        setUsersMap({});
        return;
      }

      const uniqueUserCodes = [
        ...new Set(
          auditLogsList.items
            .map(item => item.user_code)
            .filter(code => code && code.trim() !== "")
        ),
      ];

      if (uniqueUserCodes.length === 0) {
        setUsersMap({});
        return;
      }

      const { cached, missing } = getUsersWithCache(uniqueUserCodes);

      setUsersMap(cached);

      if (missing.length === 0) {
        return;
      }

      setIsLoadingUsers(true);
      try {
        const userPromises = missing.map(userCode =>
          auditLogsService.listMsplogisticUsers({
            params: {
              page: 1,
              "per-page": 10,
              sort: "-maker_date",
              keywords: "",
              expand: "maker,checker",
              fields: "",
              columnFilter: {
                "filter[code]": userCode,
              },
            },
          })
        );

        const userResponses = await Promise.all(userPromises);
        const newUsers = {};

        userResponses.forEach((res, index) => {
          const userCode = missing[index];
          const userData = res?.data?.items?.[0] || res?.data?.[0] || null;
          if (userData) {
            newUsers[userCode] = userData;
          }
        });

        if (Object.keys(newUsers).length > 0) {
          cacheUsers(newUsers);
        }

        setUsersMap({ ...cached, ...newUsers });
      } catch (e) {
        setUsersMap(cached);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [auditLogsList]);

  useEffect(() => {
    if (auditLogsParams?.run) {
      listAuditLogs();
    }
  }, [auditLogsParams]);

  useEffect(() => {
    initColumn();
  }, [t, usersMap, isLoadingUsers, logAdminUsers]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const existingToken = localStorage.getItem(AUDIT_TRACKING_TOKEN_KEY);

        if (!existingToken) {
          const res = await auditLogsService.getAuditToken();
          const idToken = res?.data?.id_token ?? null;
          if (idToken) {
            localStorage.setItem(AUDIT_TRACKING_TOKEN_KEY, idToken);
          }
        }
      } catch (e) {
      } finally {
        if (mounted) {
          setAuditLogsParams(prev => ({ ...prev, run: true }));
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const formatTimestamp = timestamp => {
    if (!timestamp) return "-";
    try {
      const timestampMs =
        typeof timestamp === "string"
          ? parseInt(timestamp) * 1000
          : timestamp * 1000;
      const date = new Date(timestampMs);
      return date.toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      return timestamp;
    }
  };

  const logAdminUsersOptions = useMemo(() => {
    const options = [];
    if (logAdminUsers?.items) {
      logAdminUsers.items.forEach(user => {
        const displayName = user.display_name || user.username || user.email;
        options.push({
          label: displayName,
          value: user.code,
          userData: user,
        });
      });
    }
    return options;
  }, [logAdminUsers]);

  const logAdminUsersEmailOptions = useMemo(() => {
    const options = [];
    if (logAdminUsers?.items) {
      logAdminUsers.items.forEach(user => {
        if (user.email) {
          options.push({
            label: user.email,
            value: user.code,
            userData: user,
          });
        }
      });
    }
    return options;
  }, [logAdminUsers]);

  const initColumn = () => {
    const columns = [
      {
        key: "user_name",
        dataField: "user_code",
        text: t("table.user_name"),
        filter: customFilter({
          type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
        }),
        filterRenderer: (onFilter, column) => (
          <CustomSelectFilter
            onFilter={onFilter}
            column={column}
            paramFilter={auditLogsParams.columnFilter}
            dataSelect={logAdminUsersOptions}
            placeholder={t("placeholder.select", {
              title: t("table.user_name"),
            })}
          />
        ),
        formatter: (cell, row) => {
          const userInfo = usersMap[row.user_code];
          return userInfo?.display_name || userInfo?.username || "-";
        },
        allowExport: true,
      },
      {
        key: "user_email",
        dataField: "user_code",
        text: t("table.user_email"),
        headerStyle: { width: "250px" },
        filter: customFilter({
          type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
        }),
        filterRenderer: (onFilter, column) => (
          <CustomSelectFilter
            onFilter={onFilter}
            column={column}
            paramFilter={auditLogsParams.columnFilter}
            dataSelect={logAdminUsersEmailOptions}
            placeholder={t("placeholder.select", {
              title: t("table.user_email"),
            })}
          />
        ),
        formatter: (cell, row) => {
          const userInfo = usersMap[row.user_code];
          return userInfo?.email || "-";
        },
        allowExport: true,
      },
      {
        dataField: "action",
        text: t("table.action"),
        sort: true,
        align: "center",
        headerStyle: { width: "160px" },
        filter: selectFilter({
          placeholder: t("placeholder.select", { title: t("table.action") }),
          options: selectOptionsTable("action"),
        }),
        formatter: (cell, row) => {
          const actionValue = row.action;
          const action = auditLogActions.find(item => item.key === actionValue);
          return action ? action.label : actionValue || "-";
        },
        allowExport: true,
      },
      {
        dataField: "type",
        text: t("table.type"),
        sort: true,
        align: "center",
        headerStyle: { width: "160px" },
        filter: selectFilter({
          placeholder: t("placeholder.select", { title: t("table.type") }),
          options: selectOptionsTable("type"),
        }),
        formatter: (cell, row) => {
          const typeValue = row.type || row.entity_type || "";
          if (!typeValue) return "-";

          let type = auditLogTypes.find(item => item.key === typeValue);

          if (!type && typeValue) {
            type = auditLogTypes.find(
              item => item.key.toUpperCase() === typeValue.toUpperCase()
            );
          }

          if (!type && typeValue) {
            const upperValue = typeValue.toUpperCase();
            type = auditLogTypes.find(item => {
              const upperKey = item.key.toUpperCase();
              return (
                upperValue.includes(upperKey) || upperKey.includes(upperValue)
              );
            });
          }

          return type ? type.label : typeValue;
        },
        filterValue: (cell, row) => {
          const typeValue = row.type || row.entity_type || "";
          if (!typeValue) return "";

          let type = auditLogTypes.find(item => item.key === typeValue);

          if (!type && typeValue) {
            type = auditLogTypes.find(
              item => item.key.toUpperCase() === typeValue.toUpperCase()
            );
          }

          if (!type && typeValue) {
            const upperValue = typeValue.toUpperCase();
            type = auditLogTypes.find(item => {
              const upperKey = item.key.toUpperCase();
              return (
                upperValue.includes(upperKey) || upperKey.includes(upperValue)
              );
            });
          }

          return type ? type.label : typeValue;
        },
        allowExport: true,
      },
      {
        dataField: "maker_date",
        text: t("table.created_at"),
        sort: true,
        align: "center",
        headerStyle: { width: "160px" },
        formatter: (cell, row) => {
          return formatTimestamp(row.maker_date);
        },
        filter: customFilter({
          type: CUSTOM_DATE_FILTER_TABLE_TYPE,
        }),
        filterRenderer: (onFilter, column) => (
          <CustomDateFilter
            onFilter={onFilter}
            column={column}
            paramFilter={auditLogsParams.columnFilter}
            showOnlyOptions={["clear", "eq"]}
          />
        ),
        allowExport: true,
      },
    ];
    setColumnsTable(columns);
  };

  const selectOptionsTable = (name = "") => {
    const select = {};
    switch (name) {
      case "action":
        auditLogActions.forEach(item => {
          select[item.key] = item.label;
        });
        break;
      case "type":
        auditLogTypes.forEach(item => {
          select[item.key] = item.label;
        });
        break;
      default:
        break;
    }
    return select;
  };

  const handleTableChange = useCallback(
    (type, { page, sizePerPage, sortField, sortOrder, columnFilter }) => {
      const column_filter = columnFilter || {};
      const cleanedFilters = {};
      let shouldResetPage = false;

      const isFilterChange = type !== "pagination" && type !== "sort";

      Object.keys(column_filter).forEach(key => {
        const value = column_filter[key];

        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          value !== " "
        ) {
          if (typeof value === "object" && value !== null && "label" in value) {
            if (key === "user_code") {
              const user =
                logAdminUsers?.items?.find(u => u.code === value.value) ||
                logAdminUsers?.items?.find(u => u.email === value.value);

              if (user) {
                cleanedFilters["filter[user_code]"] = user.code;
                if (isFilterChange) {
                  shouldResetPage = true;
                }
              }
            }
          } else if (key === "action") {
            cleanedFilters["action"] = value;
            if (isFilterChange) {
              shouldResetPage = true;
            }
          } else if (key === "type") {
            cleanedFilters["type"] = value;
            if (isFilterChange) {
              shouldResetPage = true;
            }
          } else if (
            key === "filter[maker_date][gte]" ||
            key === "filter[maker_date][eq]"
          ) {
            cleanedFilters["filter[maker_date]"] = value;
            if (isFilterChange) {
              shouldResetPage = true;
            }
          } else if (key === "filter[maker_date][lte]") {
            return;
          } else if (key !== "user_code") {
            cleanedFilters[key] = value;
            if (isFilterChange) {
              shouldResetPage = true;
            }
          }
        } else {
          if (
            key === "user_code" ||
            key === "action" ||
            key === "type" ||
            key === "filter[maker_date][gte]" ||
            key === "filter[maker_date][eq]"
          ) {
            if (isFilterChange) {
              shouldResetPage = true;
            }
          }
        }
      });

      let apiSortField = sortField;
      if (sortField === "audit_action") {
        apiSortField = "action";
      }

      setAuditLogsParams(prev => ({
        ...prev,
        sort: apiSortField ? sortOrder + apiSortField : prev.sort,
        columnFilter: cleanedFilters,
        page: shouldResetPage ? 1 : page || 1,
        sizePerPage: sizePerPage || 10,
        run: true,
      }));
    },
    [logAdminUsers]
  );

  const onSearchAllHandle = value => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setAuditLogsParams(prev => ({
        ...prev,
        keywords: value,
        page: 1,
        run: true,
      }));
    }, 500);
  };

  const toggleDetailModal = () => {
    setDetailModal(!detailModal);
  };

  const onClickView = (row, index) => {
    setDataDetail(row);
    setMode("view");
    toggleDetailModal();
  };

  const rowEventsHandler = {
    onDoubleClick: (e, row, rowIndex) => {
      setDataDetail(row);
      setMode("view");
      toggleDetailModal();
    },
  };

  const listRowAction = [
    {
      icon: <i className="fas fa-eye fa-lg text-info ms-2" />,
      text: t("table.actions.view"),
      onClick: onClickView,
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.list")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={`${t("title.list")} (${auditLogsList?.totalCount || 0})`}
            breadcrumbItems={[
              { name: t("title.list") },
              { name: "Danh sách Audit Logs" },
            ]}
          />

          <Row>
            <Col className="col-12">
              <DetailAuditLogs
                dataDetail={dataDetail}
                isOpenModal={detailModal}
                toggleModal={toggleDetailModal}
                usersMap={usersMap}
              />
            </Col>
            <Col className="col-12">
              <ExportAuditLogsModal
                isOpen={exportModal}
                toggle={toggleExportModal}
                onExport={handleExport}
              />
            </Col>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <RemoteDataTable
                    keyField="id"
                    data={auditLogsList?.items || []}
                    columns={columnsTable}
                    page={auditLogsParams.page}
                    sizePerPage={auditLogsParams.sizePerPage}
                    totalSize={auditLogsList?.totalCount || 0}
                    onTableChange={handleTableChange}
                    onSearchAllHandle={onSearchAllHandle}
                    listRowAction={listRowAction}
                    loading={isLoading || isLoadingUsers}
                    isAuditLogsPage={true}
                    rowEvents={rowEventsHandler}
                  >
                    <Button
                      color="primary"
                      size="md"
                      onClick={toggleExportModal}
                      className=" flex-shrink-0"
                      style={{ height: "38px" }}
                    >
                      <i className="fas fa-file-export me-2"></i>
                      {t("button.export")}
                    </Button>
                  </RemoteDataTable>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ListAuditLogs;
